import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPhrase, callWithAuth, putPhraseSaved } from "@/api/Api";

/**
 * Custom hook to fetch phrase translations.
 *
 * @param startVideoWordId - The ID of the first word in the phrase
 * @param endVideoWordId - The ID of the last word in the phrase
 * @param enabled - Whether to automatically fetch the phrase (defaults to true)
 * @returns React Query result object with phrase data, loading and error states
 */
export function usePhrase(
  startVideoWordId: number | undefined,
  endVideoWordId: number | undefined,
  testingString: string,
  enabled = true
) {
  const queryClient = useQueryClient();
  const lowerID = Math.min(
    startVideoWordId ?? Infinity,
    endVideoWordId ?? Infinity
  );
  const higherID = Math.max(
    startVideoWordId ?? -Infinity,
    endVideoWordId ?? -Infinity
  );
  const phraseQuery = useQuery({
    queryKey: ["phrase", lowerID, higherID],
    queryFn: async () => {
      if (startVideoWordId === undefined || endVideoWordId === undefined) {
        return {
          phrase: "",
          isPhraseSaved: false,
          phraseId: 0,
        };
      }
      const res = await callWithAuth(getPhrase, {
        startVideoWordId,
        endVideoWordId,
        testingString,
      });
      return res;
    },
    enabled: enabled && startVideoWordId !== -1 && endVideoWordId !== -1,
    staleTime: 5 * 60 * 1000, // Consider phrases fresh for 5 minutes
  });

  // Mutation to update phrase saved status
  const phraseSavedMutation = useMutation({
    mutationFn: ({
      isSaved,
      lessonId,
      phraseId,
    }: {
      /** the phraseID of the saved phrase */
      isSaved: boolean;
      /** the lessonID of the saved phrase */
      lessonId: number;
      phraseId?: number;
    }) => {
      if (phraseId === undefined) {
        throw new Error("Cannot save phrase without valid word IDs");
      }
      if (startVideoWordId === undefined || endVideoWordId === undefined) {
        throw new Error("Cannot save phrase without valid word IDs");
      }
      return callWithAuth(putPhraseSaved, {
        isPhraseSaved: isSaved,
        lessonId,
        phraseId,
      });
    },
    // Optimistically update the cache
    onMutate: async ({ isSaved }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["phrase", lowerID, higherID],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([
        "phrase",
        lowerID,
        higherID,
      ]);

      // Optimistically update to the new value
      if (previousData) {
        queryClient.setQueryData(["phrase", lowerID, higherID], {
          ...previousData,
          isPhraseSaved: isSaved,
        });
      }

      return { previousData };
    },
    // If the mutation fails, roll back to the previous value
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["phrase", lowerID, higherID],
          context.previousData
        );
      }
    },
  });

  return {
    phraseTranslation: phraseQuery.data?.phrase,
    isPhraseSaved: phraseQuery.data?.isPhraseSaved || false,
    phraseId: phraseQuery.data?.phraseId || undefined,
    setIsPhraseSaved: phraseSavedMutation.mutate,
    isLoading: phraseQuery.isLoading,
    isError: phraseQuery.isError,
    error: phraseQuery.error,
    refetch: phraseQuery.refetch,
  };
}
